import { useState, useCallback, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Trans, t } from '@lingui/macro';
import _ from 'lodash';

import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Tooltip } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

import FormSection from '../../form/FormSection';
import { useDataService } from '../../../services/ApiDataService';

import NgpTextField from '../../form/NgpTextField';
import NgpEmailField from '../../form/NgpEmailField';
import NgpForm from '../../form/NgpForm';
import { useFind } from '../../../services/FormQueryService';
import useUiAlerts, { createAlertConfig, ERROR } from '../../../stores/AlertsStore';

import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { GENERIC_SERVER_ERROR, useErrorWrapper } from '../../../services/FormErrorService';
import DataTable from '../../data-table/DataTable';
import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';

import { convertLoadParamsToQueryParams } from '../../data-table/Loaders';
import { useColumnStyles } from '../agents/AgentStyles';

import {
    CSR_COUNTRY_FIELD,
    CSR_STATE_FIELD,
    CSR_CITY_FIELD,
    CSR_ORG_FIELD,
    CSR_ORG_UNIT_FIELD,
    CSR_EMAIL_FIELD,
    CSR_DEFAULT_VALUES,
} from './CertificateCSRConstants';
import {
    AGENT_MODEL_SHORT_DISPLAY_FIELD,
    AGENT_NAME_FIELD,
    AGENT_NETWORK_HOSTNAME_FIELD,
    AGENT_SOFTWARE_VERSION_FIELD,
    AGENT_STATUS_FIELD,
    AGENT_TAGS_DISPLAY_FIELD,
} from '../agents/AgentConstants';
import { buildAgentColumns } from './CertificateAgents';
import { CERTIFICATE_USAGES } from './CertificateConstants';

const MAX_CSR_SELECTION = 1500;

function paramsToQueryString(queryParams = {}) {
    const params = new URLSearchParams();

    _.forEach(queryParams, (value, key) => {
        if (_.isArray(value)) {
            value.forEach((v) => params.append(key, v));
        } else {
            params.append(key, value);
        }
    });
    return params;
}
const countryCodeRegex = /^[A-Za-z]{2}$/i;
const fieldRegex = /^[^`~!@#$%^&*+=}{|[\]'";:<,>.?/\\]+?$/i;
const organizationNameRegex = /^[^`~!@#$%^&*+=}{|[\]'";:<>,?/\\]+?$/i;
const organizationalUnitRegex = /^[^`~!@#$%^&*+=}{|[\]'";:<>.?/\\]+?$/i;
const apiUrl = '/ipm/v1/admin/csrConfiguration';

const dataSourceParams = {
    searchFields: [
        AGENT_NAME_FIELD,
        AGENT_STATUS_FIELD,
        AGENT_TAGS_DISPLAY_FIELD,
        AGENT_MODEL_SHORT_DISPLAY_FIELD,
        AGENT_NETWORK_HOSTNAME_FIELD,
    ],
    sort: AGENT_SOFTWARE_VERSION_FIELD,
    rowLimit: 2000,
    rowStart: 0,
};

export default function CertificateCSRDialog({ open, setOpen, certUsage }) {
    const [csrConfigured, setCsrConfigured] = useState(false);
    const [csrConfigExists, setCsrConfigExists] = useState(true);
    const { errorWrapper } = useErrorWrapper();
    const [, uiAlertActions] = useUiAlerts();
    const [tableSelection, setTableSelection] = useState(undefined);
    const [isSelectionTooLarge, setIsSelectionTooLarge] = useState(false);
    const [isSelectionValid, setIsSelectionValid] = useState(false);

    const classes = useColumnStyles();
    const gridColumns = buildAgentColumns(classes);

    const onClose = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues: CSR_DEFAULT_VALUES,
        mode: 'onChange',
    });

    const { reset, handleSubmit, formState } = methods;
    const { isValid } = formState;
    const handleDownloadCSR = () => {
        const params = paramsToQueryString({
            searchFields: dataSourceParams.searchFields,
            sort: dataSourceParams.sort,
            search: searchRef.current,
        });

        params.append('selectionParams', JSON.stringify(tableSelection));
        params.append('t', Date.now().toString());
        params.append('certUsage', certUsage);
        onClose();
        setTimeout(() => {
            window.location.href = `/ipm/v1c/CSRGenerate/downloadCSRZip?${params.toString()}`;
        }, 0);
    };
    const agentCacheRef = useRef([]);
    const countRef = useRef(0);
    const searchRef = useRef('');

    const updateSelection = (selectedIds) => {
        agentCacheRef.current = selectedIds;
    };

    const dataService = useDataService();
    const loadAgents = useCallback(
        (params) => {
            const queryParams = {
                ...dataSourceParams,
                ...(certUsage !== CERTIFICATE_USAGES.voip && { nextGen: true }),
                ...convertLoadParamsToQueryParams(params),
            };
            searchRef.current = queryParams.search || '';

            return dataService.get('/query/admin/agents', queryParams).then((res) => {
                const agents = res?.data || [];
                const count = res?.count || 0;
                countRef.current = count;
                return { rows: agents, length: count };
            });
        },
        [dataService, certUsage]
    );

    const postFindHandler = (response) => {
        if (!response[0]) {
            setCsrConfigExists(false);
        } else {
            setCsrConfigExists(true);
        }
        return response[0] || _.first(response) || response;
    };

    const { formData } = useFind({
        url: apiUrl,
        postFindHandler,
        resetFunc: reset,
        defaultValues: CSR_DEFAULT_VALUES,
        errorHandler: errorWrapper({}),
    });

    const handleTableSelectionChange = useCallback((currentTableSelection) => {
        setTableSelection(currentTableSelection);

        const { inverted, selection } = currentTableSelection;
        const totalCount = countRef.current;

        const selectedCount = inverted ? Math.max(0, totalCount - selection.length) : selection.length;

        setIsSelectionValid(selectedCount > 0);
        setIsSelectionTooLarge(selectedCount > MAX_CSR_SELECTION);
    }, []);

    const handleCSRSubmit = async (data) => {
        setCsrConfigured(true);
        try {
            const response = csrConfigExists
                ? await dataService.put(`/ipm/v1/admin/csrConfiguration/${formData._id}`, data)
                : await dataService.post('/ipm/v1/admin/csrConfiguration', data);

            return response;
        } catch (error) {
            const errorMessage = _.get(error, 'responseBody.message') || GENERIC_SERVER_ERROR;
            uiAlertActions.createAlert(createAlertConfig(ERROR, errorMessage));
            return null;
        }
    };

    const renderCSRForm = () => (
        <FormSection title={<Trans>CSR Details</Trans>}>
            <NgpTextField
                fieldName={CSR_COUNTRY_FIELD}
                label={<Trans>Country Code</Trans>}
                required
                inputProps={{ size: 64, maxLength: 64 }}
                fieldRules={{
                    pattern: {
                        value: countryCodeRegex,
                        message: t`Not a valid country code, must contain two letters.`,
                    },
                }}
            />

            <NgpTextField
                fieldName={CSR_STATE_FIELD}
                label={<Trans>State/Province</Trans>}
                required
                inputProps={{ size: 64, maxLength: 64 }}
                fieldRules={{
                    pattern: {
                        value: fieldRegex,
                        message: t`Not a valid state/province name, only ( ) _ - are allowed as special characters.`,
                    },
                }}
            />

            <NgpTextField
                fieldName={CSR_CITY_FIELD}
                label={<Trans>City/Loclity</Trans>}
                inputProps={{ size: 64, maxLength: 64 }}
                fieldRules={{
                    pattern: {
                        value: fieldRegex,
                        message: t`Not a valid city/locality name, only ( ) _ - are allowed as special characters.`,
                    },
                }}
            />

            <NgpTextField
                fieldName={CSR_ORG_FIELD}
                label={<Trans>Organization Name</Trans>}
                required
                inputProps={{ size: 64, maxLength: 64 }}
                fieldRules={{
                    pattern: {
                        value: organizationNameRegex,
                        message: t`Not a valid organization name, only ( ) _ - . are allowed as special characters.`,
                    },
                }}
            />

            <NgpTextField
                fieldName={CSR_ORG_UNIT_FIELD}
                label={<Trans>Organizational Unit</Trans>}
                inputProps={{ size: 64, maxLength: 64 }}
                helpTipText={t`Organizational Unit accepts multiple values separated by comma.`}
                fieldRules={{
                    pattern: {
                        value: organizationalUnitRegex,
                        message: t`Not a valid organizational unit, only ( ) _ - , are allowed as special characters.`,
                    },
                }}
            />
            <NgpEmailField
                fieldName={CSR_EMAIL_FIELD}
                label={<Trans>Email Address</Trans>}
                inputProps={{
                    size: 64,
                    maxLength: 64,
                }}
            />
            <div style={{ fontWeight: '500', marginTop: '16px' }}>
                <Trans>Common Name will default to nPoint name, appended with the certificate usage.</Trans>
            </div>
        </FormSection>
    );
    const renderAgentTable = () => {
        return (
            <DataTable
                loader={loadAgents}
                columns={gridColumns}
                visibleRows={10}
                limit={5000}
                shrink={false}
                disableCSVExport
                disableColumnRemover
                showSelectionColumn
                selectionOptions={{
                    mode: SELECTIONMODE_MULTI,
                    idMapping: '_id',
                    allowSelectAll: true,
                    onChange: handleTableSelectionChange,
                }}
                emptyText={t`No data`}
                updateSelection={updateSelection}
            />
        );
    };
    return (
        <Dialog id="csr-dialog" open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Trans>{`Generate Certificate Signing Requests for ${certUsage} Certificates`}</Trans>
                <IconButton aria-label="cancel" onClick={onClose} style={{ float: 'right' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <FormProvider {...methods}>
                    <NgpForm initialized showSave={false} showRequiredFieldsTip={false}>
                        {!csrConfigured ? renderCSRForm() : renderAgentTable()}
                    </NgpForm>
                </FormProvider>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>CANCEL</Button>

                <Button disabled={!csrConfigured} onClick={() => setCsrConfigured(false)}>
                    Previous
                </Button>

                <Button
                    disabled={csrConfigured || !isValid}
                    color="primary"
                    variant="contained"
                    onClick={handleSubmit(handleCSRSubmit)}
                >
                    SAVE & CONTINUE
                </Button>
                <Tooltip title={isSelectionTooLarge ? t`Only up to 1,500 CSRs can be downloaded at once.` : ''}>
                    <span>
                        <Button
                            disabled={!csrConfigured || isSelectionTooLarge || !isSelectionValid}
                            color="primary"
                            variant="contained"
                            onClick={handleDownloadCSR}
                        >
                            DOWNLOAD CSR
                            {isSelectionTooLarge && <HelpOutlineIcon fontSize="small" style={{ marginLeft: 4 }} />}
                        </Button>
                    </span>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
}
